import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { username: "", password: "", full_name: "", role: "vendedor", banca_id: "", taquilla_id: "", commission_pct: 0, active: true };
const ROLES = [
  { v: "admin", l: "Administrador" },
  { v: "banca_owner", l: "Dueño de banca" },
  { v: "taquilla_operator", l: "Operador de taquilla" },
  { v: "vendedor", l: "Vendedor" },
];

export default function VendedoresPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });
  const { data: bancas = [] } = useQuery({ queryKey: ["bancas"], queryFn: async () => (await api.get("/bancas")).data });
  const { data: taquillas = [] } = useQuery({ queryKey: ["taquillas"], queryFn: async () => (await api.get("/taquillas")).data });

  const pwdMismatch = !!confirmPwd && confirmPwd !== form.password;
  const pwdReady = (editing && !form.password) || (!!form.password && confirmPwd === form.password);

  const filteredTaq = taquillas.filter((t) => !form.banca_id || t.banca_id === form.banca_id);

  const save = useMutation({
    mutationFn: async (raw) => {
      // Nombre derivado del usuario (edición conserva el existente); comisión heredada de la agencia
      const uname = raw.username.trim();
      const payload = { ...raw, full_name: (editing && raw.full_name?.trim()) || uname, commission_pct: editing ? Number(editing.commission_pct || 0) : 0 };
      // La clave se envía tal cual (sin trim ni transformaciones): el hash debe coincidir con lo tecleado.
      const p = { ...payload, username: payload.username.trim() };
      if (!p.banca_id) delete p.banca_id;
      if (!p.taquilla_id) delete p.taquilla_id;
      if (editing) {
        if (!p.password) delete p.password;
        return (await api.patch(`/users/${editing.id}`, p)).data;
      }
      return (await api.post("/users", p)).data;
    },
    onSuccess: (u) => {
      toast.success(`${editing ? "Usuario actualizado" : "Usuario creado"}: ${u?.username || ""}`);
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => {
      toast.success("Usuario eliminado");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al eliminar"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY });
    setConfirmPwd("");
    setShowPwd(false);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username, password: "", full_name: u.full_name, role: u.role,
      banca_id: u.banca_id || "", taquilla_id: u.taquilla_id || "",
      commission_pct: u.commission_pct || 0, active: u.active,
    });
    setConfirmPwd("");
    setShowPwd(false);
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Vendedores y usuarios</h1>
          <p className="text-sm text-muted-foreground">Gestión de accesos por rol y asignaciones a taquillas.</p>
        </div>
        <Button onClick={openCreate} data-testid="user-create-button"><Plus size={16} className="mr-1.5"/>Nuevo Usuario</Button>
      </div>

      <Card className="fintech-card overflow-hidden" data-testid="admin-table-vendedores">
        <div className="overflow-x-auto">
          <table className="w-full text-sm fintech-table">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Taquilla</th>
                <th className="px-4 py-3 text-right">Comisión %</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground">Cargando…</td></tr>}
              {users.map((u) => {
                const taq = taquillas.find((t) => t.id === u.taquilla_id);
                return (
                  <tr key={u.id} className="fintech-row">
                    <td className="px-4 py-3 font-mono text-sky-600 dark:text-sky-300">{u.username}</td>
                    <td className="px-4 py-3 font-medium">{u.full_name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs border border-border bg-secondary/60">{ROLES.find(r => r.v === u.role)?.l || u.role}</span></td>
                    <td className="px-4 py-3 text-muted-foreground">{taq ? `${taq.name} (${taq.code})` : "-"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{u.commission_pct || 0}%</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${u.active ? "border-emerald-500/30 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10" : "border-rose-500/30 text-rose-700 dark:text-rose-300 bg-rose-500/10"}`}>
                        {u.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`user-edit-${u.username}`}><Pencil size={16}/></Button>
                        <Button variant="ghost" size="icon" className="text-rose-700 dark:text-rose-300" onClick={() => { if (window.confirm(`Eliminar usuario ${u.username}?`)) del.mutate(u.id); }} data-testid={`user-delete-${u.username}`}><Trash2 size={16}/></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Usuario</Label>
              <Input value={form.username} onChange={(e) => setForm({...form, username: e.target.value.toLowerCase().replace(/\s/g, "")})} disabled={!!editing} autoComplete="off" spellCheck={false} data-testid="user-form-username"/>
              {!editing && form.username && (
                <p className="text-xs text-muted-foreground" data-testid="user-form-username-preview">Iniciará sesión como: <span className="font-mono font-semibold text-foreground">{form.username.trim()}</span></p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Contraseña {editing && <span className="text-xs text-muted-foreground">(dejar vacío para no cambiar)</span>}</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} autoComplete="new-password" spellCheck={false} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="pr-10" data-testid="user-form-password"/>
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"} data-testid="user-form-password-toggle">
                  {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            {(!editing || form.password) && (
              <div className="space-y-2 md:col-start-2">
                <Label>Confirmar contraseña</Label>
                <Input type={showPwd ? "text" : "password"} autoComplete="new-password" spellCheck={false} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} data-testid="user-form-password-confirm"/>
                {pwdMismatch && <p className="text-xs text-destructive" data-testid="user-form-password-mismatch">Las contraseñas no coinciden</p>}
              </div>
            )}
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger data-testid="user-form-role"><SelectValue/></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Banca</Label>
              <Select value={form.banca_id} onValueChange={(v) => setForm({...form, banca_id: v, taquilla_id: ""})}>
                <SelectTrigger><SelectValue placeholder="Sin asignar"/></SelectTrigger>
                <SelectContent>
                  {bancas.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Taquilla</Label>
              <Select value={form.taquilla_id} onValueChange={(v) => setForm({...form, taquilla_id: v})} disabled={!form.banca_id}>
                <SelectTrigger><SelectValue placeholder={form.banca_id ? "Selecciona…" : "Primero elige banca"}/></SelectTrigger>
                <SelectContent>
                  {filteredTaq.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} id="active-u" />
              <Label htmlFor="active-u">Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => save.mutate(form)} disabled={save.isPending || !pwdReady} data-testid="user-form-submit">{editing ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
